import React from "react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { fmtDate } from "../../lib/format";
import { Link } from "react-router-dom";
import { Users, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

const Tile = ({ label, value, icon: Icon, accent, testid }) => (
  <Card className="border-zinc-200 shadow-none" data-testid={testid}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
            {label}
          </div>
          <div className="font-mono text-4xl font-medium text-zinc-950 mt-3 tabular-nums">
            {value}
          </div>
        </div>
        <div className={`h-9 w-9 rounded-md grid place-items-center ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-zinc-500">Loading...</div>;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">
          Overview
        </div>
        <h1 className="font-display font-bold text-3xl text-zinc-950 mt-1">
          Admin Dashboard
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          Live snapshot of all client portfolios.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile testid="tile-active" label="Active Portfolios" value={data.active_count} icon={CheckCircle2} accent="bg-green-50 text-green-700" />
        <Tile testid="tile-inactive" label="Inactive" value={data.inactive_count} icon={Users} accent="bg-zinc-100 text-zinc-700" />
        <Tile testid="tile-expiring" label="Expiring ≤ 60d" value={data.expiring_soon_count} icon={Clock} accent="bg-amber-50 text-amber-700" />
        <Tile testid="tile-undefined" label="Value Not Set" value={data.undefined_value_count} icon={AlertTriangle} accent="bg-red-50 text-red-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-zinc-200 shadow-none">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-display font-semibold text-zinc-950">Expiring Within 60 Days</div>
              <Badge className="bg-amber-50 text-amber-700 border-amber-200">{data.expiring_soon_count}</Badge>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead className="text-right">Days Left</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.expiring_soon.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-zinc-500">None</TableCell></TableRow>
                )}
                {data.expiring_soon.map((p) => (
                  <TableRow key={p.portfolio_id}>
                    <TableCell>
                      <Link className="text-zinc-950 hover:underline" to={`/admin/clients/${p.client_id}`}>
                        {p.client?.name || p.client_id}
                      </Link>
                      <div className="text-xs text-zinc-500">{p.client?.email}</div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{fmtDate(p.end_date)}</TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={p.days_to_expiry < 30 ? "text-loss" : "text-warn"}>
                        {p.days_to_expiry}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-none">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-display font-semibold text-zinc-950">Initial Value Pending</div>
              <Badge className="bg-red-50 text-red-700 border-red-200">{data.undefined_value_count}</Badge>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead className="text-right">Start</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.undefined_value.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-zinc-500">None</TableCell></TableRow>
                )}
                {data.undefined_value.map((p) => (
                  <TableRow key={p.portfolio_id}>
                    <TableCell>
                      <Link className="text-zinc-950 hover:underline" to={`/admin/clients/${p.client_id}`}>
                        {p.client?.name || p.client_id}
                      </Link>
                      <div className="text-xs text-zinc-500">{p.client?.email}</div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{p.client?.mobile || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtDate(p.start_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
